import type { InventoryDay, Workspace } from '../domain/hotel';

// Date-only hotel values never go through a browser's local timezone.
export function addDays(value: string, amount: number): string {
    const date = new Date(`${value}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + amount);
    return date.toISOString().slice(0, 10);
}
export function nightsBetween(start: string, end: string): number {
    return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000);
}
export function formatDate(value: string, options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }): string {
    return new Intl.DateTimeFormat('en', { ...options, timeZone: 'UTC' }).format(new Date(`${value}T12:00:00Z`));
}
export function formatTimestamp(value: string): string {
    return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Aruba' }).format(new Date(value)) + ' AST';
}
export function money(amount: number, currency = 'AWG', compact = false): string {
    const formatted = new Intl.NumberFormat('en', {
        minimumFractionDigits: compact && amount % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(amount / 100);
    return `${currency} ${formatted}`;
}
export function moneyNumber(amount: number): string {
    return new Intl.NumberFormat('en', { maximumFractionDigits: 2 }).format(amount / 100);
}
// Parse decimal input without multiplying a binary float. Also used for basis points.
export function parseDecimal(value: string, allowNegative = false): number | null {
    const match = (allowNegative ? /^(-?)(\d+)(?:\.(\d{1,2}))?$/ : /^()(\d+)(?:\.(\d{1,2}))?$/).exec(value.trim());
    if (!match) return null;
    const result = Number(match[2]) * 100 + Number((match[3] ?? '').padEnd(2, '0'));
    return Number.isSafeInteger(result) ? (match[1] === '-' ? -result : result) : null;
}
export function decimal(amount: number): string { return (amount / 100).toFixed(2); }
export function percentage(basisPoints: number): string { return `${basisPoints / 100}%`; }
export function titleCase(value: string): string { return value.replaceAll('_', ' ').replace(/\b\w/g, char => char.toUpperCase()); }
export function initials(name: string): string { return name.split(/\s+/).slice(0, 2).map(word => word[0]).join('').toUpperCase(); }

export function bookedRooms(data: Workspace, roomID: string, date: string): number {
    return data.reservations.reduce((total, reservation) => total + (
        reservation.status === 'confirmed' && reservation.offer.room_id === roomID && reservation.offer.check_in <= date && date < reservation.offer.check_out
            ? reservation.offer.quantity : 0
    ), 0);
}
export function availableRooms(data: Workspace, day: InventoryDay): number {
    return day.capacity - day.unavailable - bookedRooms(data, day.room_id, day.date);
}
export function protectedRooms(data: Workspace, roomID: string, date: string): number {
    return data.allotments.reduce((total, item) => {
        const contract = data.contracts.find(value => value.id === item.contract_id);
        if (item.room_id !== roomID || date < item.start || date > item.end || !contract?.active || data.today >= addDays(date, -item.release_days)) return total;
        const used = data.reservations.filter(value => value.status === 'confirmed' && value.offer.contract_id === item.contract_id && value.offer.room_id === roomID && value.offer.check_in <= date && date < value.offer.check_out)
            .reduce((sum, value) => sum + value.offer.quantity, 0);
        return total + Math.max(0, item.quantity - used);
    }, 0);
}

export function csvValue(value: unknown): string {
    let text = String(value ?? '');
    // Prevent formula execution when exports are opened in a spreadsheet.
    if (/^[\s]*[=+\-@\t\r\n]/.test(text)) text = `'${text}`;
    return `"${text.replaceAll('"', '""')}"`;
}
export function downloadCSV(filename: string, headers: string[], rows: unknown[][]): void {
    const contents = '\uFEFF' + [headers, ...rows].map(row => row.map(csvValue).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
